/**
 * 建筑实例化渲染池 —— 从 main.ts 逐字迁出。
 *
 * 拆分第三刀，也是**第一块真正持有状态的**：`POOLS` 与 `bid` 正是引发过
 * TDZ 的那类模块级集合（G009 的 `cableObjs` 是同一类问题）。搬进独立模块后，
 * 消费者必须 import，ES 模块按依赖顺序初始化，声明位置不再由文件行号决定。
 *
 * 外部依赖用**注入**而不是 import：`city` / `shadowDirty` / `TYPE_COLOR`
 * 都住在 main.ts，直接 import 会形成循环。注入让依赖方向单向：
 * main.ts → building-pool，反之没有。
 *
 * 安全网：e2e「实例池满时扩容而不是丢弃：实例全部入池，网格对象数不增」。
 */
import * as THREE from 'three';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = any;

const asMesh = (o: THREE.Object3D): THREE.Mesh & {
  geometry: THREE.BufferGeometry;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  material: THREE.Material & Record<string, any>;
} => o as never;

/** 池需要但住在 main.ts 的东西。initBuildingPool 注入一次。 */
interface PoolDeps {
  /** 全部建筑的场景父节点。 */
  readonly city: AnyObj;
  /** 通知阴影贴图需要重画（场景几乎全静态，按需重画）。 */
  readonly shadowDirty: () => void;
  /** 建筑类型 -> 信标颜色。 */
  readonly typeColor: Record<string, number>;
}
let deps: PoolDeps;
export function initBuildingPool(d: PoolDeps): void { deps = d; }

/* ================= 建筑实例化渲染池 =================
   clone(true) 让每栋建筑独立提交 8~14 个 mesh：示范局 ~140 栋
   ≈ 1160 次 draw call/帧，阴影 pass 再翻一倍 —— 这是全场最大的单项开销
   （GPU 三角形 20 万根本不是瓶颈，提交次数才是）。
   改为每类型一个渲染池：同类建筑共享 InstancedMesh，一种材质一次提交，
   全城建筑降到 类型数×材质数 ≈ 150 次。
   每栋建筑只保留一个【空的锚点 Group】承载 userData/等级牌/选中逻辑；
   几何全部住在池里，实例矩阵 = 锚点矩阵 × 模板局部矩阵。 */
/* 初始容量；满了按 2 倍扩容而不是拒绝 —— 见 poolGrow。
   InstancedMesh 的容量在构造时固定，所以扩容 = 重建 + 搬数据。 */
const POOL_CAP=192;                       // 每类型上限；21 类共 ~2.2MB 矩阵缓冲，可放心给足
const POOLS:Record<string,AnyObj>={};    // type -> {meshes:[{im,local}], records:[anchor], topY}
const _WHITE=new THREE.Color(1,1,1);
function buildPool(type:string,tpl:AnyObj,withBeacon=true){
  if(POOLS[type])return POOLS[type];
  tpl.updateMatrixWorld(true);
  const meshes:AnyObj[]=[];
  tpl.traverse((o:AnyObj)=>{if(o.isMesh){
    const im=new THREE.InstancedMesh(asMesh(o).geometry,asMesh(o).material,POOL_CAP);
    im.count=0; im.castShadow=true; im.receiveShadow=true;
    // 实例包围球不会随矩阵更新，整池会被一起剔除掉，必须禁用
    im.frustumCulled=false;
    // instanceColor 缓冲初始为 0（全黑），必须先刷成白
    for(let i=0;i<POOL_CAP;i++)im.setColorAt(i,_WHITE);
    im.userData.poolType=type;
    meshes.push({im,local:o.matrixWorld.clone()});
    deps.city.add(im);
  }});
  // 类型信标做成池里的一个"伪网格"：与建筑同槽位，同样一次提交
  if(withBeacon){
    const bim=new THREE.InstancedMesh(new THREE.SphereGeometry(1.6,8,8),
      // transparent 是为了按缩放距离淡出（updateBeacons）；不透明材质改 opacity 无效
      new THREE.MeshBasicMaterial({color:deps.typeColor[type]||0xffffff,transparent:true}),POOL_CAP);
    bim.count=0; bim.frustumCulled=false;
    bim.userData.poolType=type; bim.userData.isBeacon=true;
    deps.city.add(bim);
    meshes.push({im:bim,local:new THREE.Matrix4().makeTranslation(0,30,0)});
  }
  const box=new THREE.Box3().setFromObject(tpl);
  // 记住信标那条记录本身而非 im —— poolGrow 扩容时只替换 rec.im，引用依然有效
  POOLS[type]={meshes,records:[],topY:box.max.y,cap:POOL_CAP,
    beaconRec:withBeacon?meshes[meshes.length-1]:null};
  return POOLS[type];
}
const _poolM=new THREE.Matrix4();
function writePoolMatrices(anchor:AnyObj){
  const pool=POOLS[anchor.userData.bType],slot=anchor.userData.slot;
  if(!pool||slot==null)return;
  anchor.updateMatrix();
  for(const {im,local} of pool.meshes){
    _poolM.copy(anchor.matrix).multiply(local);
    im.setMatrixAt(slot,_poolM); im.instanceMatrix.needsUpdate=true;
    /* Three.js 的 InstancedMesh.raycast() 用【网格级】boundingSphere 做早退剔除
       （与 geometry.boundingSphere 是两码事）：早退检查写的是
       `if(this.boundingSphere===null)this.computeBoundingSphere()` —— 只在
       null 时才重算。若这个属性曾经被算过一次（哪怕当时 count 还是 0，算出
       的是半径 -1 的退化空球），它就不再是 null，raycast 会一直用那个空球，
       而空球永远 intersectsSphere()==false —— 结果是点击建筑本体【永远】选不中，
       只有不走这条剔除路径的其他物体（发光球、地形）能被点中。
       上面 poolGrow 里 frustumCulled=false 的注释只顾到了渲染剔除，没顾到这条
       独立的 raycast 剔除路径。修法：矩阵一变就把它置回 null，下次
       raycast 会用当前 count 懒重算，成本几乎为零。 */
    im.boundingSphere=null;
  }
}
/* 实例池扩容：容量翻倍并把已有矩阵/颜色原样搬过去。
   不扩容的老行为是「池满 -> 返回 false」：建筑照样进内核、照样扣料，
   但没有网格 —— 玩家花钱买到一个隐形建筑。建筑受 ZONE 限制单类型最多 180 格
   碰不到上限，但基建（管道/输送带每格 2~3 段）共用同一套池且不受 ZONE 约束，
   铺满约 70 格就会越界，是真实可达的。 */
function poolGrow(type:string){
  const pool=POOLS[type]; if(!pool)return;
  const next=pool.cap*2;
  for(const rec of pool.meshes){
    const old=rec.im;
    const im=new THREE.InstancedMesh(old.geometry,old.material,next);
    im.count=old.count;
    im.frustumCulled=false;                      // 与 buildPool 一致：实例包围球不随矩阵更新
    im.castShadow=old.castShadow; im.receiveShadow=old.receiveShadow;
    im.userData.poolType=old.userData.poolType;
    im.userData.isBeacon=old.userData.isBeacon;
    im.instanceMatrix.array.set(old.instanceMatrix.array);
    im.instanceMatrix.needsUpdate=true;
    for(let i=0;i<next;i++)im.setColorAt(i,_WHITE);   // 先建满白色缓冲
    if(old.instanceColor&&im.instanceColor){
      im.instanceColor.array.set(old.instanceColor.array);   // 再覆盖已用段的 tint
      im.instanceColor.needsUpdate=true;
    }
    deps.city.add(im); deps.city.remove(old);
    old.dispose();                               // 只释放实例缓冲；几何/材质与模板共享
    rec.im=im;
  }
  pool.cap=next;
  deps.shadowDirty();
}
function poolAlloc(type:string,anchor:AnyObj){
  const pool=POOLS[type]; if(!pool)return false;
  if(pool.records.length>=pool.cap)poolGrow(type);
  anchor.userData.slot=pool.records.length;
  pool.records.push(anchor);
  for(const {im} of pool.meshes)im.count=pool.records.length;
  writePoolMatrices(anchor);
  deps.shadowDirty();
  return true;
}
function poolFree(anchor:AnyObj){
  const pool=POOLS[anchor.userData.bType],slot=anchor.userData.slot;
  if(!pool||slot==null)return;
  const last=pool.records.length-1,moved=pool.records[last];
  if(slot!==last){                        // 尾槽换补：矩阵与 tint 一起搬过来
    pool.records[slot]=moved; moved.userData.slot=slot;
    writePoolMatrices(moved);
    const c=new THREE.Color();
    for(const {im} of pool.meshes){
      if(!im.instanceColor)continue;
      im.getColorAt(last,c); im.setColorAt(slot,c); im.instanceColor.needsUpdate=true;
    }
  }
  pool.records.pop();
  for(const {im} of pool.meshes)im.count=pool.records.length;
  anchor.userData.slot=null;
  deps.shadowDirty();
}
/* 实例高亮：instanceColor 是【乘算】，无法做 emissive 叠加，
   用 >1 的分量把选中建筑推亮（bloom 阈值 0.5 之下，不会糊成光斑）。 */
function poolTint(anchor:AnyObj,c:AnyObj){
  const pool=POOLS[anchor.userData.bType],slot=anchor.userData.slot;
  if(!pool||slot==null)return;
  for(const {im} of pool.meshes){
    if(im.userData.isBeacon)continue;
    im.setColorAt(slot,c||_WHITE);
    if(im.instanceColor)im.instanceColor.needsUpdate=true;
  }
}

export {
  POOL_CAP, POOLS, buildPool, poolGrow, poolAlloc, poolFree, poolTint, writePoolMatrices,
};
